import React from 'react'
import { fmt } from './format'

// Hero ring: value 0..100, white on the hero band.
export const Ring = ({ value, label }) => {
    const r = 38
    const c = 2 * Math.PI * r
    const empty = value === null || value === undefined || value === ''
    const v = empty ? 0 : Math.max(0, Math.min(Number(value) || 0, 100))
    const off = c * (1 - v / 100)
    return (
        <svg width="92" height="92" viewBox="0 0 92 92" aria-hidden="true">
            <circle cx="46" cy="46" r={r} fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="9" />
            <circle
                cx="46"
                cy="46"
                r={r}
                fill="none"
                stroke="#fff"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={c.toFixed(1)}
                strokeDashoffset={off.toFixed(1)}
                transform="rotate(-90 46 46)"
            />
            <text x="46" y={label ? 44 : 51} textAnchor="middle" fontSize="19" fontWeight="800" fill="#fff">
                {empty ? '–' : Math.round(v)}
            </text>
            {label && (
                <text x="46" y="59" textAnchor="middle" fontSize="9" fill="#c3cff7">
                    {label}
                </text>
            )}
        </svg>
    )
}

const W = 560
const H = 200
const L = 34
const R = 14
const T = 16
const B = 28

// Score trend (daily average %). Points: [{ Label, Value }]
export const ScoreLine = ({ points }) => {
    const pts = (points || []).filter(p => p && p.Value !== null && p.Value !== undefined)
    if (pts.length < 2) {
        return <div className="empty">Not enough scored {pts.length === 1 ? 'days' : 'data'} for a trend yet.</div>
    }
    const vals = pts.map(p => Number(p.Value))
    const min = Math.max(0, Math.floor((Math.min(...vals) - 5) / 10) * 10)
    const max = 100
    const n = pts.length
    const x = i => L + ((W - L - R) * i) / (n - 1)
    const y = v => T + ((H - T - B) * (max - v)) / Math.max(max - min, 1)
    const xy = vals.map((v, i) => [x(i), y(v)])
    const line = xy.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
    const area = `${line} L${xy[n - 1][0].toFixed(1)} ${y(min).toFixed(1)} L${xy[0][0].toFixed(1)} ${y(min).toFixed(1)} Z`
    const grid = []
    for (let v = min; v <= max; v += max - min > 40 ? 20 : 10) grid.push(v)
    const last = xy[n - 1]
    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="200" role="img" aria-label="Average score trend">
            {grid.map(v => (
                <g key={v}>
                    <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--hd-line)" />
                    <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--hd-mut)">{v}</text>
                </g>
            ))}
            {min < 85 && (
                <g>
                    <line x1={L} x2={W - R} y1={y(85)} y2={y(85)} stroke="var(--hd-ok)" strokeDasharray="4 4" />
                    <text x={L + 6} y={y(85) - 5} fontSize="11" fill="var(--hd-ok)">target 85</text>
                </g>
            )}
            <path d={area} fill="var(--hd-primary-soft)" />
            <path d={line} fill="none" stroke="var(--hd-primary)" strokeWidth="2" strokeLinejoin="round" />
            {xy.map((p, i) => (
                <circle key={pts[i].Label || i} cx={p[0]} cy={p[1]} r="3" fill="var(--hd-primary)">
                    <title>{`${pts[i].Label}: ${vals[i]}%`}</title>
                </circle>
            ))}
            <circle cx={last[0]} cy={last[1]} r="4.5" fill="var(--hd-primary)" stroke="var(--hd-card)" strokeWidth="2" />
            <text x={last[0]} y={last[1] - 10} textAnchor="middle" fontSize="12" fontWeight="700" fill="var(--hd-fg)">
                {vals[n - 1]}%
            </text>
            <text x={L} y={H - 8} fontSize="11" fill="var(--hd-mut)">{pts[0].Label}</text>
            <text x={W - R} y={H - 8} textAnchor="end" fontSize="11" fill="var(--hd-mut)">{pts[n - 1].Label}</text>
        </svg>
    )
}

// Findings per week. Points: [{ Label, Value }] (12 weekly points, Monday-based)
export const FindingsBars = ({ points, label = 'findings' }) => {
    const weeks = points || []
    if (!weeks.length) return <div className="empty">No completed {label} data yet.</div>
    const n = weeks.length
    const vals = weeks.map(w => Number(w.Value) || 0)
    const max = Math.max(...vals, 1) * 1.15
    const slot = (W - L - R) / n
    const bw = Math.min(28, slot * 0.62)
    const y = v => T + (H - T - B) * (1 - v / max)
    // about 5 grid lines at any volume: step = 1, 2 or 5 x a power of ten
    const raw = max / 5
    const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1)))
    const m = raw / pow
    let step = 10 * pow
    if (m <= 1) step = pow
    else if (m <= 2) step = 2 * pow
    else if (m <= 5) step = 5 * pow
    const grid = []
    for (let v = 0; v <= max; v += step) grid.push(v)
    const avg = vals.reduce((a, v) => a + v, 0) / n
    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="200" role="img" aria-label={`${label} per week`}>
            {grid.map(v => (
                <g key={v}>
                    <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--hd-line)" />
                    <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--hd-mut)">{v}</text>
                </g>
            ))}
            <line x1={L} x2={W - R} y1={y(avg)} y2={y(avg)} stroke="var(--hd-mut)" strokeDasharray="4 4" />
            {weeks.map((w, i) => {
                const bx = L + slot * i + (slot - bw) / 2
                const h = H - B - y(vals[i])
                const isLast = i === n - 1
                return (
                    <g key={w.Label || i}>
                        <rect
                            x={bx.toFixed(1)}
                            y={y(vals[i]).toFixed(1)}
                            width={bw.toFixed(1)}
                            height={Math.max(h, 0).toFixed(1)}
                            rx="3"
                            fill={isLast ? 'var(--hd-primary)' : 'var(--hd-primary-soft)'}
                            stroke={isLast ? 'none' : 'var(--hd-primary)'}
                            strokeWidth="1"
                        >
                            <title>{`Week of ${w.Label}: ${vals[i]} ${label}`}</title>
                        </rect>
                        {isLast && (
                            <text x={(bx + bw / 2).toFixed(1)} y={(y(vals[i]) - 6).toFixed(1)} textAnchor="middle" fontSize="12" fontWeight="700" fill="var(--hd-fg)">
                                {vals[i]}
                            </text>
                        )}
                    </g>
                )
            })}
            <text x={L} y={H - 8} fontSize="11" fill="var(--hd-mut)">{weeks[0].Label}</text>
            <text x={W - R} y={H - 8} textAnchor="end" fontSize="11" fill="var(--hd-mut)">this week</text>
        </svg>
    )
}

export const StatusDonut = ({ mix, unitWord }) => {
    const parts = [
        ['Completed', Number(mix?.Completed || 0), 'var(--hd-ok)'],
        ['In progress', Number(mix?.InProcess || 0), 'var(--hd-primary)'],
        ['Created', Number(mix?.Created || 0), 'var(--hd-mut)'],
        ['Overdue', Number(mix?.Overdue || 0), 'var(--hd-bad)'],
    ].filter(p => p[1] > 0 || p[0] !== 'Overdue')
    const total = parts.reduce((a, p) => a + p[1], 0)
    const r = 34
    const c = 2 * Math.PI * r
    let acc = 0
    return (
        <div className="mix">
            <svg width="104" height="104" viewBox="0 0 92 92" aria-hidden="true">
                {total === 0 ? (
                    <circle cx="46" cy="46" r={r} fill="none" stroke="var(--hd-line)" strokeWidth="12" />
                ) : (
                    parts.map(p => {
                        const len = (c * p[1]) / total
                        const seg = (
                            <circle
                                key={p[0]}
                                cx="46"
                                cy="46"
                                r={r}
                                fill="none"
                                stroke={p[2]}
                                strokeWidth="12"
                                strokeDasharray={`${Math.max(len - 2, 0).toFixed(1)} ${c.toFixed(1)}`}
                                strokeDashoffset={(-acc).toFixed(1)}
                                transform="rotate(-90 46 46)"
                            />
                        )
                        acc += len
                        return seg
                    })
                )}
                <text x="46" y="43" textAnchor="middle" fontSize="18" fontWeight="800" fill="var(--hd-fg)">{fmt(total)}</text>
                <text x="46" y="58" textAnchor="middle" fontSize="10" fill="var(--hd-mut)">{unitWord}</text>
            </svg>
            <ul>
                {parts.map(p => (
                    <li key={p[0]}>
                        <i style={{ background: p[2] }} />
                        {p[0]}
                        <b>{fmt(p[1])}</b>
                    </li>
                ))}
            </ul>
        </div>
    )
}
